<template>
    <div>
        <t-loader :loading="isBusy"></t-loader>
        <div class="row">
            <div class="col-12">
                <div class="invoice-filter-container">
                    <div class="filter"
                         :class="{active: state === selectedState, failed: state === 'App\\States\\Invoice\\PaymentFailed'}"
                         v-for="state in states"
                         @click.prevent="selectState(state)">
                        <span>{{ state | stateToName | titleLang | lang }}</span>
                        <div class="count" v-if="getCount(state) > 0">{{ getCount(state) }}</div>
                    </div>
                    <div class="mobile-filter">
                        <label for="mobile-select-state">Filter invoices</label>
                        <div class="form-group">
                            <select v-model="selectedState" class="form-control" id="mobile-select-state">
                                <option v-for="state in statesDivided" :value="state" :disabled="state === 'disabled'">
                                    {{ state | stateToName | titleLang | lang }}{{ getMobileCount(state) }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <div class="card mt-2">
                    <div class="card-header">
                        {{ selectedState | stateToName | titleLang | lang }}
                        <br>
                        <small>All invoices in this state</small>
                    </div>
                    <div class="card-body">
                        <b-table
                                show-empty
                                :responsive="true"
                                :items="myProvider"
                                :busy.sync="isBusy"
                                :fields="fields"
                                :current-page="currentPage"
                                :per-page="perPage"
                                :filter="selectedState"
                                ref="table"
                        >
                            <template v-slot:cell(total)="data">
                                {{ data.item.sum | formatCurrency(data.item.currency) }}
                            </template>
                            <template v-slot:cell(created_at)="data">
                                {{ cleanTimestamp(data.item.created_at) }}
                            </template>
                            <template v-slot:cell(quick_pay)="data">
                              <span v-if="data.item.quick_pay === 1" class="text-info">
                                Requested
                              </span>
                              <span v-else-if="data.item.quick_pay === 2" class="text-success">
                                Approved
                              </span>
                              <span v-else-if="data.item.quick_pay === 0" class="text-danger">
                                Rejected
                              </span>
                              <span v-else>
                                -
                              </span>
                            </template>
                            <template v-slot:cell(actions)="data">
                                <template v-for="action in data.item.actions">
                                    <a :href="action.url" class="mr-2" v-if="action.action === 'delete'"
                                       @click.prevent="deleteInvoice(action.url)">{{ action.action }}</a>
                                    <a :href="action.url" class="mr-2" v-else>{{ action.action }}</a>
                                </template>
                            </template>
                        </b-table>
                        <b-pagination
                                v-model="currentPage"
                                :total-rows="totalRows"
                                :per-page="perPage"
                                aria-controls="my-table"
                        ></b-pagination>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

export default {
  name: 'InvoiceTabs',
  props: {
    states: {
      type: Array,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    count: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      filter: null,
      invoices: [],
      selectedState: 'App\\States\\Invoice\\Draft',
      isBusy: false,
      defaultFields: [
        {key: 'id', label: '#'},
        {key: 'company', label: 'Company'},
        {key: 'created_at', label: 'Created at'},
        {key: 'total', label: 'Total'},
        {key: 'actions', label: 'Actions'},
      ],
    };
  },
  computed: {
    statesDivided() {
      const states = Array.from(this.states);
      states.splice(2, 0, 'disabled');

      return states;
    },
    fields() {
      let fields = this.defaultFields;
      if (this.isNotAdmin) {
        return fields;
      }
      fields = cloneDeep(fields);
      fields.splice(1, 0, {key: 'user.name', label: 'Talent'});
      fields.splice(5, 0, {key: 'quick_pay', label: 'Quick Pay'});

      return fields;
    },
    isNotAdmin() {
      return !this.isAdmin;
    },
  },
  filters: {
    stateToName(state) {
      return state.split('\\').pop();
    },
    titleLang: (word) => {
      return `states.client.${word}.title`;
    },
  },
  methods: {
    timeSinceTimestamp(ts) {
      return moment(ts).fromNow();
    },
    cleanTimestamp(ts) {
      return moment(ts).format("YYYY-MM-DD HH:mm")
    },
    selectState(state) {
      if (this.isBusy) {
        return;
      }
      this.selectedState = state;
    },
    myProvider(ctx) {
      const url = `/invoices/tab?page=${ctx.currentPage}&size=${ctx.perPage}&filter=${ctx.filter}`;
      let promise = axios.get(url);

      return promise.then((response) => {
        const data = response.data.data;
        const meta = response.data.meta;
        this.currentPage = meta.current_page;
        this.totalRows = meta.total;
        this.invoices = data || [];

        return this.invoices;
      }).catch(error => {
        return [];
      });
    },
    getCount(state) {
      if (this.count.hasOwnProperty(state)) {
        return this.count[state];
      }

      return 0;
    },
    getMobileCount(state) {
      const count = this.getCount(state);
      if (count > 0) {
        return ` (${count})`;
      }
    },
    deleteInvoice(url) {
      if (this.isBusy) {
        return;
      }

      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this action.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Delete Invoice'
      }).then((result) => {
        if (result.isConfirmed) {
          this.isBusy = true;
          axios.delete(url).then(response => {
            this.$toasted.info('Invoice deleted.');
          }).catch(error => {
            this.$toasted.error('Could not delete invoice.');
          }).finally(() => {
            this.isBusy = false;
            this.refresh();
          });
        }
      })
    },
    refresh() {
      this.$nextTick(() => {
        this.$refs.table.refresh();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.invoice-filter-container {
  .filter {
    &.failed {
      .count {
        background: #b30505;
      }
    }
  }
}
</style>
